import { useEffect, useState } from "react";
import { Loader2, SlidersVertical, Waves, AudioLines, Gauge, Mic2, Sparkles, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { ReverbEditor, StripEditor, ModulationEditor, SidechainEditor, LimiterEditor } from "@/components/MixBusModules";
import PresetCompare from "@/components/PresetCompare";

let optionsCache = null;
let mineCache = null;
const mineListeners = new Set();
const publishMine = (list) => { mineCache = list; mineListeners.forEach((fn) => fn(list)); };
const MODULES = [
  ["strip", "Channel strip", SlidersVertical, "EQ 6 bandes · gate · compresseur · saturation · auto-volume (type Melda)"],
  ["modulation", "Modulation", Waves, "Chorus · flanger · phaser (type Blue Cat)"],
  ["reverb", "Réverbération", AudioLines, "Convolution — espaces caverne, forêt, cathédrale… (type TAL / Valhalla)"],
  ["sidechain", "Sidechain voix", Mic2, "La musique s'abaisse quand la voix parle (ducking)"],
  ["limiter", "Limiteur", Gauge, "True-peak · lookahead · loudness cible LUFS (type LoudMax)"],
];

// Server-side Mix Bus (ffmpeg): equivalents of free studio plugins → new track/creation, free.
export default function MixBusPanel({ id, kind = "music", seq, onSaved }) {
  const t = `mixbus-${seq}`;
  const [opt, setOpt] = useState(optionsCache);
  const [voices, setVoices] = useState([]);
  const [chain, setChain] = useState({});
  const [preset, setPreset] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState(mineCache || []);
  const [saveName, setSaveName] = useState(null);
  const [compare, setCompare] = useState(false);
  useEffect(() => { mineListeners.add(setMine); return () => mineListeners.delete(setMine); }, []);
  useEffect(() => {
    api.get("/mixbus/presets").then(({ data }) => publishMine(data)).catch(() => {});
    if (!opt) api.get("/mixbus/options").then(({ data }) => { optionsCache = data; setOpt(data); }).catch(() => {});
    if (kind === "music") api.get("/voice?limit=50").then(({ data }) => setVoices((Array.isArray(data) ? data : data.items || []).filter((v) => v.status === "completed" && v.audio_path))).catch(() => {});
  }, [kind, opt]);
  if (!opt) return <div className="mt-3 text-xs text-muted-foreground flex items-center gap-2" data-testid={`${t}-loading`}><Loader2 size={12} className="animate-spin" /> Mix Bus…</div>;

  const d = opt.defaults;
  const blank = { strip: { eq: [], comp: { ...d.comp }, saturation: 0, auto_volume: false }, modulation: { ...d.modulation }, reverb: { space: "hall" }, sidechain: { ...d.sidechain, voice_id: voices[0]?.id || "" }, limiter: { ...d.limiter } };
  const toggle = (k) => { setPreset(null); setChain((c) => ({ ...c, [k]: c[k] ? null : blank[k] })); };
  const setMod = (k, v) => { setPreset(null); setChain((c) => ({ ...c, [k]: v })); };
  const applyPreset = (k, srcChain) => {
    const p = JSON.parse(JSON.stringify(srcChain || opt.presets[k].chain));
    if (p.strip) p.strip = { ...blank.strip, comp: null, ...p.strip, ...(p.strip.gate ? { gate: { ...d.gate, ...p.strip.gate } } : {}), ...(p.strip.comp ? { comp: { ...d.comp, ...p.strip.comp } } : {}) };
    for (const m of ["modulation", "reverb", "sidechain", "limiter"]) if (p[m]) p[m] = { ...blank[m], ...p[m] };
    setPreset(k); setChain(p);
  };
  const active = MODULES.filter(([k]) => chain[k]);
  const savePreset = async () => {
    if (!saveName?.trim()) return toast.error("Donne un nom au preset.");
    try {
      const body = { name: saveName.trim(), chain: Object.fromEntries(active.filter(([k]) => k !== "sidechain").map(([k]) => [k, chain[k]])) };
      const { data } = await api.post("/mixbus/presets", body);
      publishMine([data, ...mine.filter((p) => p.id !== data.id)]); setPreset(data.id); setSaveName(null);
      toast.success(`Preset « ${data.name} » enregistré — réutilisable sur toutes les pistes, voix et thèmes.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Enregistrement impossible."); }
  };
  const deletePreset = async (p) => {
    try { await api.delete(`/mixbus/presets/${p.id}`); publishMine(mine.filter((x) => x.id !== p.id)); if (preset === p.id) setPreset(null); toast.success(`Preset « ${p.name} » supprimé.`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Suppression impossible."); }
  };
  const run = async () => {
    if (!active.length) return toast.error("Active au moins un module.");
    if (chain.sidechain && !chain.sidechain.voice_id) return toast.error("Choisis une voix pour le sidechain.");
    setBusy(true);
    try {
      const body = { chain: Object.fromEntries(active.map(([k]) => [k, chain[k]])), name: name.trim() || null };
      const { data } = await api.post(kind === "music" ? `/generations/${id}/mixbus` : `/voice/${id}/mixbus`, body);
      toast.success(`Mix Bus appliqué → #${data.seq} · ${data.mixbus?.label} · QA ${(data.metadata?.qa || data.qa)?.grade || "?"} (gratuit).`);
      onSaved?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Mix Bus impossible."); }
    finally { setBusy(false); }
  };
  const editors = {
    strip: <StripEditor value={chain.strip} bands={opt.eq_bands} defaults={d} onChange={(v) => setMod("strip", v)} t={t} />,
    modulation: <ModulationEditor value={chain.modulation} types={opt.modulations} onChange={(v) => setMod("modulation", v)} t={t} />,
    reverb: <ReverbEditor value={chain.reverb} spaces={opt.spaces} onChange={(v) => setMod("reverb", v)} t={t} />,
    sidechain: <SidechainEditor value={chain.sidechain} voices={voices} onChange={(v) => setMod("sidechain", v)} t={t} />,
    limiter: <LimiterEditor value={chain.limiter} onChange={(v) => setMod("limiter", v)} t={t} />,
  };
  return (
    <div className="mt-3 rounded-xl border border-cyan-500/25 bg-cyan-500/[0.04] p-4 space-y-4" data-testid={t}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[10px] font-mono text-cyan-300 tracking-wider flex items-center gap-1.5"><SlidersVertical size={12} /> MIX BUS · FFMPEG · GRATUIT</p>
        <div className="flex flex-wrap gap-1">
          {Object.entries(opt.presets).filter(([k]) => kind === "music" || !opt.presets[k].chain.sidechain).map(([k, p]) => (
            <button key={k} type="button" onClick={() => applyPreset(k)} className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${preset === k ? "border-cyan-400 text-cyan-200 bg-cyan-500/15" : "border-border text-muted-foreground hover:text-foreground hover:border-cyan-500/40"}`} data-testid={`${t}-preset-${k}`}>{p.label}</button>
          ))}
          {mine.map((p) => (
            <span key={p.id} className={`inline-flex items-center rounded-full border text-[10px] transition-colors ${preset === p.id ? "border-fuchsia-400 text-fuchsia-200 bg-fuchsia-500/15" : "border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10"}`} data-testid={`${t}-mypreset-${p.id}`}>
              <button type="button" onClick={() => applyPreset(p.id, p.chain)} className="pl-2 pr-1 py-0.5" title={p.label}>{p.name}</button>
              <button type="button" onClick={() => deletePreset(p)} className="pr-1.5 opacity-60 hover:opacity-100" aria-label={`Supprimer ${p.name}`} data-testid={`${t}-mypreset-del-${p.id}`}><X size={10} /></button>
            </span>
          ))}
        </div>
      </div>
      {kind === "music" && (
        <div>
          <button type="button" onClick={() => setCompare((o) => !o)} className="text-[11px] text-cyan-300 hover:underline" data-testid={`${t}-compare-toggle`}>{compare ? "Masquer le comparateur" : "Comparer 2 presets côte à côte →"}</button>
          {compare && <div className="mt-2"><PresetCompare genId={id} presets={opt.presets} mine={mine} onPick={(k) => { const p = opt.presets[k]; const m = mine.find((x) => x.id === k); applyPreset(k, p ? p.chain : m?.chain); toast.success(`Preset « ${p?.label || m?.name} » chargé dans le panneau.`); }} testId={`${t}-compare`} /></div>}
        </div>
      )}
      <div className="grid sm:grid-cols-5 gap-2">
        {MODULES.filter(([k]) => kind === "music" || k !== "sidechain").map(([k, label, Icon, desc]) => (
          <button key={k} type="button" onClick={() => toggle(k)} title={desc} className={`text-left rounded-lg border px-3 py-2 transition-colors ${chain[k] ? "border-cyan-400/60 bg-cyan-500/10 text-cyan-100" : "border-border text-muted-foreground hover:border-cyan-500/40"}`} data-testid={`${t}-module-${k}`}>
            <span className="flex items-center gap-1.5 text-xs font-medium"><Icon size={13} /> {label}</span>
            <span className="block text-[10px] opacity-70 mt-0.5 truncate">{desc.split(" (")[0]}</span>
          </button>
        ))}
      </div>
      {active.map(([k, label]) => (
        <div key={k} className="rounded-lg border border-border/60 bg-background/40 p-3" data-testid={`${t}-editor-${k}`}>
          <p className="text-[10px] font-mono text-muted-foreground mb-2 uppercase">{label}</p>
          {editors[k]}
        </div>
      ))}
      <div className="flex items-center gap-2 flex-wrap">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom (optionnel)" className="h-9 max-w-xs bg-background border-border text-sm" data-testid={`${t}-name`} />
        <Button onClick={run} disabled={busy || !active.length} className="gap-2 bg-cyan-500 hover:bg-cyan-400 text-black" data-testid={`${t}-apply`}>{busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Appliquer le Mix Bus</Button>
        <span className="text-[11px] text-muted-foreground">Nouvelle {kind === "music" ? "piste" : "création"} + QA TechValidator (spectre inclus).</span>
        <span className="ml-auto flex items-center gap-1.5">
          {saveName === null ? (
            <Button variant="outline" size="sm" onClick={() => setSaveName("")} disabled={!active.filter(([k]) => k !== "sidechain").length} className="h-9 gap-1.5 border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10" data-testid={`${t}-save-preset`}><Save size={13} /> Sauver comme preset</Button>
          ) : (
            <>
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && savePreset()} placeholder="Nom du preset perso" className="h-9 w-44 bg-background border-fuchsia-500/40 text-sm" autoFocus data-testid={`${t}-preset-name`} />
              <Button size="sm" onClick={savePreset} className="h-9 bg-fuchsia-500 hover:bg-fuchsia-400 text-black" data-testid={`${t}-preset-save-confirm`}><Save size={13} /></Button>
              <Button size="sm" variant="ghost" onClick={() => setSaveName(null)} className="h-9" data-testid={`${t}-preset-save-cancel`}><X size={13} /></Button>
            </>
          )}
        </span>
      </div>
    </div>
  );
}
